import React, { useEffect, useState } from 'react';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { Card } from '../ui/card';
import { TokenUsageData, userTokenReportService } from '../../services/UserTokenReportService';

const UserTokensPieChart: React.FC = () => {
  const [tokenData, setTokenData] = useState<TokenUsageData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await userTokenReportService.refreshTokenData();
      setLoading(false);
    };
    
    fetchData();
    
    // Subscribe to future updates
    const unsubscribe = userTokenReportService.subscribe((data) => {
      setTokenData(data);
    });
    
    return () => unsubscribe();
  }, []);
  
  // Configure chart options
  const getChartOptions = (): ApexOptions => {
    return {
      chart: {
        fontFamily: "Outfit, sans-serif",
        type: "donut",
        foreColor: '#697a8d',
        toolbar: {
          show: false,
        },
      },
      plotOptions: {
        pie: {
          donut: {
            size: '75%',
            labels: {
              show: true,
              name: {
                show: true,
                fontSize: '22px',
                fontWeight: 600,
                color: undefined,
                offsetY: -10,
              },
              value: {
                show: true,
                fontSize: '16px',
                fontWeight: 400,
                color: undefined,
                offsetY: 16,
                formatter: (val) => val.toString()
              },
              total: {
                show: true,
                label: 'Total',
                fontSize: '14px',
                color: '#697a8d',
                formatter: function (w) {
                  return tokenData?.allocated.toString() || '0';
                }
              }
            }
          }
        }
      },
      colors: ['#465fff', '#4ade80'],
      labels: ['Used Tokens', 'Available Tokens'],
      dataLabels: {
        enabled: false,
      },
      legend: {
        show: true,
        position: 'bottom',
        horizontalAlign: 'center',
        fontSize: '14px',
        fontFamily: 'Outfit, sans-serif',
        markers: {
          size: 10,
          strokeWidth: 0,
        },
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: {
              height: 300,
            },
            legend: {
              position: 'bottom',
            },
          },
        },
      ],
    };
  };
  
  // Prepare chart data
  const getChartSeries = (): number[] => {
    if (!tokenData) return [0, 0];
    return [tokenData.used, tokenData.available];
  };
  
  // Format date range for display
  const formatDateRange = (): string => {
    if (!tokenData?.period.start || !tokenData?.period.end) return 'Current Period';
    
    const options: Intl.DateTimeFormatOptions = { 
      month: 'short', 
      day: 'numeric' 
    };
    
    const fromDate =new Date(tokenData.period.start);
    const toDate = new Date(tokenData.period.end);
    try { 
      return `${fromDate.toLocaleDateString('en-US', options)} - ${toDate.toLocaleDateString('en-US', options)}`;
    } catch (error) {
      console.warn("Token data:", tokenData);
      console.warn("Error while trying to format date range:", error);
      return "Invalid Date";
    }
  };
  
  // Handle loading state
  if (loading && !tokenData) {
    return (
      <Card className="p-5 h-full">
        <div className="flex justify-center items-center h-64">
          <div className="animate-pulse flex space-x-4">
            <div className="flex-1 space-y-4 py-1">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
              <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
            </div>
          </div>
        </div>
      </Card>
    );
  }
  
  return (
    <Card className="p-5 h-full">
      <div className="flex flex-col h-full">
        <div className="flex flex-col space-y-1">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Token Usage</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {formatDateRange()}
          </p>
        </div>
        
        <div className="mt-4 flex-1 min-h-[280px]">
          <Chart 
            options={getChartOptions()} 
            series={getChartSeries()} 
            type="donut"
            height={280}
          />
        </div>
        
        <div className="mt-2 grid grid-cols-2 gap-2">
          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-md">
            <p className="text-sm text-gray-500 dark:text-gray-400">Total Allocated</p>
            <p className="text-lg font-semibold text-gray-900 dark:text-white">
              {tokenData?.allocated || 0}
            </p>
          </div>
          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-md">
            <p className="text-sm text-gray-500 dark:text-gray-400">Usage Percentage</p>
            <p className="text-lg font-semibold text-gray-900 dark:text-white">
              {tokenData ? Math.round(tokenData.usedPercentage) : 0}%
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default UserTokensPieChart;